import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { tdApi } from "@src/services/api/tdApi";
import { type EntityConfig, getEntityConfig } from "@src/services/config/entityConfigs";
import type { ApiBacktest, ApiExecution, ApiJob, ApiStudy } from "@tradingdroid/frontends-types";

/**
 * Direct API pagination hook with unified loading state
 *
 * Features:
 * - Handles BOTH initial fetch AND pagination with single loading state
 * - Uses API's `next` URL for pagination continuation
 * - Auto-refetches when queryFilter changes
 * - No caching - always fetches from server
 *
 * Usage:
 * ```tsx
 * const { fetchNextPage, loading, hasMore } = useApiPagination({
 *   entityName: 'executions',
 *   currentItems: allExecutions,
 *   setItems: setExecutions,
 *   queryFilter: convertRunFiltersToQuery(filters)
 * });
 * ```
 */

interface UsePaginationResult {
  /** Fetch next batch of items (pagination) */
  fetchNextPage: () => Promise<void>;

  /** Whether there are more items to fetch */
  hasMore: boolean;

  /** Whether ANY fetch is in progress (initial OR pagination) */
  loading: boolean;

  /** Error from last fetch */
  error: Error | null;

  /** The API returned a `next` link this walk already followed, so paging stopped */
  stalled: boolean;

  /** Reset pagination state */
  reset: () => void;
}

// Type map for entity-specific API responses - enables proper type inference
type PaginatedEntityMap = {
  executions: ApiExecution;
  backtests: ApiBacktest;
  studies: ApiStudy;
  jobs: ApiJob;
};

type PaginatedEntityName = keyof PaginatedEntityMap;

interface UseApiPaginationOptions<E extends PaginatedEntityName> {
  entityName: E;
  currentItems: PaginatedEntityMap[E][];
  setItems: (items: PaginatedEntityMap[E][]) => void;
  queryFilter?: string;
}

export const useApiPagination = <E extends PaginatedEntityName>({
  entityName,
  currentItems,
  setItems,
  queryFilter,
}: UseApiPaginationOptions<E>): UsePaginationResult => {
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [stalled, setStalled] = useState(false);

  // Store the `next` URL from API response for pagination
  const nextUrlRef = useRef<string | null>(null);
  // Every `next` link this walk has followed. A link served twice means the cursor stopped moving,
  // and following it again would append the same page on every click.
  const followedUrlsRef = useRef(new Set<string>());

  // Identifies the newest request. A response whose id no longer matches has been superseded and
  // must not write. Paired with a synchronous in-flight flag, since `loading` state lags a tick.
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);

  // Track the previous walk identity (entity + filter) to detect changes
  const prevWalkRef = useRef(`${entityName}:${queryFilter ?? ""}`);

  // CRITICAL: Use refs to always access LATEST values
  // This avoids stale closure issues during async pagination fetches
  // Written after commit, never during render, so a discarded render can't leak into the
  // committed one.
  const queryFilterRef = useRef(queryFilter);
  const currentItemsRef = useRef(currentItems);
  useLayoutEffect(() => {
    queryFilterRef.current = queryFilter;
    currentItemsRef.current = currentItems;
  });

  // Helper to fetch items (used by both initial and pagination)
  const fetchItems = useCallback(
    async (isInitial: boolean) => {
      // Two next-page clicks in one tick both read `loading === false`, because state has not
      // re-rendered yet, so the state flag alone lets the same page append twice. An initial
      // fetch (mount or filter change) is allowed through: it supersedes whatever is in flight.
      if (!isInitial && inFlightRef.current) return;

      // Every response is checked against this before it is allowed to write. A slow response for
      // an older filter must not overwrite a newer one that already landed.
      const requestId = ++requestIdRef.current;
      inFlightRef.current = true;

      // Always use the ref to get the CURRENT filter value
      const currentFilter = queryFilterRef.current;

      setLoading(true);
      setError(null);

      try {
        const config = getEntityConfig(entityName as "backtests") as EntityConfig;
        const idField = config.idField;

        type EntityItem = PaginatedEntityMap[E];
        type ApiResponse = { items: EntityItem[]; next?: string };
        let resp: ApiResponse | null = null;

        // For pagination, use the `next` URL from previous response
        if (!isInitial && nextUrlRef.current) {
          followedUrlsRef.current.add(nextUrlRef.current);
          resp = (await tdApi.getApiUrl(nextUrlRef.current)) as ApiResponse;
        } else {
          // Initial fetch or no next URL - use standard API call
          const queryParams = {
            q: currentFilter || undefined,
            limit: config.pageSize,
            sort_by: `${idField}:DESC`,
          };

          if (entityName === "executions") {
            resp = (await tdApi.getExecutions({
              ...queryParams,
              embed: "live_metrics",
            })) as ApiResponse;
            // Unify live_metrics into metrics
            if (resp?.items) {
              for (const item of resp.items as ApiExecution[]) {
                item.metrics = item.live_metrics || item.metrics;
              }
            }
          } else if (entityName === "backtests") {
            resp = (await tdApi.getBacktests(queryParams)) as ApiResponse;
          } else if (entityName === "studies") {
            resp = (await tdApi.getStudies({
              ...queryParams,
              embed: (config as { embedParam?: string }).embedParam,
            })) as ApiResponse;
          } else if (entityName === "jobs") {
            resp = (await tdApi.getJobs(queryParams)) as ApiResponse;
          }
        }

        // A newer request started while this one was in flight, so its results are the ones the
        // user is waiting for. Drop this response instead of overwriting them.
        if (requestId !== requestIdRef.current) return;

        const newItems = resp?.items || [];

        const repeated = !!resp?.next && followedUrlsRef.current.has(resp.next);
        // Store next URL for pagination
        nextUrlRef.current = repeated ? null : resp?.next || null;
        if (repeated) setStalled(true);

        if (newItems.length > 0) {
          if (isInitial) {
            setItems(newItems);
          } else {
            setItems([...currentItemsRef.current, ...newItems]);
          }
          // Has more if there's a next URL
          setHasMore(!!nextUrlRef.current);
        } else {
          if (isInitial) {
            setItems([]);
          }
          setHasMore(false);
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err as Error);
      } finally {
        if (requestId === requestIdRef.current) {
          inFlightRef.current = false;
          setLoading(false);
        }
      }
    },
    [entityName, setItems], // currentItems & queryFilter accessed via refs
  );

  // Initial fetch on mount + refetch when filter changes
  useEffect(() => {
    // A new entity or a new filter is a new walk; either one drops the old cursor and guard.
    const walk = `${entityName}:${queryFilter ?? ""}`;
    const walkChanged = prevWalkRef.current !== walk;
    prevWalkRef.current = walk;

    if (walkChanged) {
      setHasMore(true);
      setStalled(false);
      // Drop the old filter's cursor. Left alone it survives a failed refetch, and the next
      // page request would then paginate the previous filter.
      nextUrlRef.current = null;
      followedUrlsRef.current.clear();
    }

    // Fetch initial data
    fetchItems(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, queryFilter]); // Only refetch on entity or filter change

  const fetchNextPage = useCallback(async () => {
    if (loading || !hasMore || currentItemsRef.current.length === 0) return;
    // No cursor means the current filter has no confirmed first page yet, which happens when its
    // refetch failed. Without this, the next-page call falls through to a first-page query and
    // appends the new filter's rows onto the old filter's.
    if (!nextUrlRef.current) return;
    await fetchItems(false);
  }, [loading, hasMore, fetchItems]);

  const reset = useCallback(() => {
    setHasMore(true);
    setError(null);
  }, []);

  return { fetchNextPage, hasMore, loading, error, stalled, reset };
};
